import { useTranslation } from "../state/i18n";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { IntegrationPropertyContainer } from "../components/field/propertyContainer/PropertyContainer";
import { IntegrationPropertyContainerOverlay } from "../components/field/propertyContainer/PropertyContainerOverlay";
import { useFormServices } from "../services/formServices";
import { makeEventListener } from "@solid-primitives/event-listener";
import IntegrationShowEvent from "../events/IntegrationShowEvent";
import { isEmpty, isNil } from "lodash-es";
import { DestructiveModal } from "../components/modal/Modal";
import type { IncompleteIntegrationDefinition, IntegrationDefinition } from "./types";
import { IntegrationVerification } from "./IntegrationVerification";
import InlineMessage from "../components/inlineMessage/InlineMessage";
import { useModalManager } from "../components/modal/ModalManager";

export const initIntegrationDefinition = <T, DTO>(
  integrationDefinition: IncompleteIntegrationDefinition<T, DTO>,
): IntegrationDefinition<T, DTO> => ({
  PreviewItem: (props) => {
    const { t } = useTranslation();
    const { open } = useModalManager();
    const { integrationService, validationService } = useFormServices();
    const [isIntegrationOpen, setIsIntegrationOpen] = createSignal(false);
    const id = untrack(() => props.id);
    const integration = createMemo(() => integrationService.getIntegration<T>(id)!);
    const issues = createMemo(() => validationService.getIssuesForIntegration(id));
    const globalIssues = createMemo(() => issues().filter((error) => isNil(error.path)));
    const hasErrors = createMemo(() => !isEmpty(issues()));

    createEffect(() => {
      makeEventListener<Record<string, IntegrationShowEvent>>(
        document,
        `${IntegrationShowEvent.name}-${id.toString()}`,
        (event) => {
          setIsIntegrationOpen(true);
          document.getElementById(event.integrationId.toString())?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        },
      );
    });

    return (
      <IntegrationPropertyContainer
        label={integration().name}
        typeLabel={t(integrationDefinition.titleKey)}
        open={isIntegrationOpen()}
        setOpen={setIsIntegrationOpen}
        icon={integrationDefinition.icon}
        Content={() => (
          <>
            <integrationDefinition.EditItem
              integration={integration()}
              updateConfiguration={(property, value) => {
                integrationService.updateConfiguration(id, property, value);
              }}
              getError={(property) =>
                issues().find(
                  (error) => error.path?.find((path) => ("key" in path ? path.key === property : false)) !== undefined,
                )?.message
              }
              propertyKeys={integrationDefinition.propertyKeys}
            />
            <Show when={!isEmpty(globalIssues())}>
              <InlineMessage variant="danger">
                <span>{t(($) => $.INTEGRATIONS_GLOBAL_ISSUES_DESCRIPTION)}</span>
                <ul class="ml-5 list-outside list-disc">
                  <For each={globalIssues()}>{(error) => <li>{error.message}</li>}</For>
                </ul>
              </InlineMessage>
            </Show>
            <Show when={integrationDefinition.verifiable}>
              <IntegrationVerification
                integration={integration()}
                integrationType={integrationDefinition}
                hasErrors={hasErrors()}
              />
            </Show>
          </>
        )}
        error={hasErrors()}
        updateName={(value) => integrationService.updateName(id, value)}
        deleteItem={() =>
          open({
            content: () => (
              <DestructiveModal
                title={t(($) => $.INTEGRATIONS_DELETE_MODAL_TITLE)}
                description={t(($) => $.INTEGRATIONS_DELETE_MODAL_DESCRIPTION)}
                confirmLabel={t(($) => $.DELETE_BUTTON)}
                cancelLabel={t(($) => $.CANCEL_BUTTON)}
                onConfirm={() => {
                  integrationService.deleteIntegration(id);
                }}
              />
            ),
          })
        }
        draggable={{
          id: id.toString(),
        }}
      />
    );
  },
  OverlayItem: (props) => {
    const { integrationService } = useFormServices();
    const id = untrack(() => props.id);
    const integration = createMemo(() => integrationService.getIntegration<T>(id)!);

    return <IntegrationPropertyContainerOverlay label={integration().name} icon={integrationDefinition.icon} />;
  },
  ...integrationDefinition,
});
